import { BASE_API_SIGN } from "../_const";
import { MessageResponse } from "../../model/MessageResponse";
import { SignaturePage } from "../../model/SignPage";

// Phần mềm ký số HacomSign chạy trên máy người ký
const PLUGIN_URL = "http://localhost:5265";
// Trình duyệt có thể treo request tới localhost khi chờ người dùng cho phép truy cập mạng nội bộ
const PLUGIN_CHECK_TIMEOUT_MS = 10000;
const PLUGIN_INFO_TIMEOUT_MS = 8000;
const VERSION_PATTERN = /^\d+(\.\d+){0,3}$/;

// Dùng khi API không trả được cấu hình; giá trị thật lấy từ HacomSign:MinVersion / HacomSign:DownloadUrl phía API
const FALLBACK_PLUGIN_INFO: HacomSignPluginInfo = {
  minVersion: "1.0.7",
  downloadUrl:
    "https://cdn-files.hacom.vn/hacom/extension/App-win/HacomSignSetup.exe",
};

export interface HacomSignPluginInfo {
  minVersion: string;
  downloadUrl: string;
}

export type HacomSignPluginStatus = "ready" | "outdated" | "notRunning";

export interface HacomSignPluginCheck extends HacomSignPluginInfo {
  status: HacomSignPluginStatus;
  // Bản 1.0.6 trở về trước không có /version nên không biết số bản
  installedVersion?: string;
}

export interface SignPosition {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export type PluginSignResult =
  | { ok: true; companyOrPerson: string }
  | { ok: false; message: string };

export function isValidVersion(version: unknown): version is string {
  return typeof version === "string" && VERSION_PATTERN.test(version);
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// Cấu hình sai (vd "v1.0.8") mà so sánh lỏng sẽ cho mọi bản qua, nên chỉ nhận số bản đúng định dạng
export function toPluginInfo(data: unknown): HacomSignPluginInfo | undefined {
  const info = data as Partial<HacomSignPluginInfo> | undefined;
  const minVersion = info?.minVersion;
  if (!isValidVersion(minVersion)) return undefined;
  const downloadUrl =
    typeof info?.downloadUrl === "string" && info.downloadUrl.startsWith("https://")
      ? info.downloadUrl
      : FALLBACK_PLUGIN_INFO.downloadUrl;
  return { minVersion, downloadUrl };
}

// Gọi thẳng fetch: repositorySign.get luôn chờ 2,5 giây và xoá thông báo đang hiện. Cấu hình ít đổi nên chỉ giữ lại khi lấy được cấu hình hợp lệ.
let pluginInfoRequest: Promise<HacomSignPluginInfo> | undefined;
function getPluginInfo(): Promise<HacomSignPluginInfo> {
  pluginInfoRequest ??= fetch(`${BASE_API_SIGN}/api/v1/HacomSign/plugin-info`, {
    signal: AbortSignal.timeout(PLUGIN_INFO_TIMEOUT_MS),
  })
    .then((r) => r.json() as Promise<MessageResponse<unknown>>)
    .then((res) => {
      const info = res?.success ? toPluginInfo(res.data) : undefined;
      if (info) return info;
      console.error("Cấu hình HacomSign không hợp lệ, dùng mặc định:", res);
      pluginInfoRequest = undefined;
      return FALLBACK_PLUGIN_INFO;
    })
    .catch((error) => {
      console.error("Không lấy được cấu hình HacomSign, dùng mặc định:", error);
      pluginInfoRequest = undefined;
      return FALLBACK_PLUGIN_INFO;
    });
  return pluginInfoRequest;
}

export async function checkHacomSignPlugin(): Promise<HacomSignPluginCheck> {
  const versionRequest = fetch(`${PLUGIN_URL}/version`, {
    signal: AbortSignal.timeout(PLUGIN_CHECK_TIMEOUT_MS),
  }).catch(() => undefined);
  const [info, response] = await Promise.all([getPluginInfo(), versionRequest]);
  if (!response) return { ...info, status: "notRunning" };
  // Bản cũ trả 404 "Endpoint không hợp lệ" cho /version
  if (response.status === 404) return { ...info, status: "outdated" };
  if (!response.ok) return { ...info, status: "notRunning" };
  const body = await response.json().catch(() => null);
  const installedVersion = isValidVersion(body?.version) ? body.version : undefined;
  if (!installedVersion) return { ...info, status: "outdated" };
  return {
    ...info,
    installedVersion,
    status:
      compareVersions(installedVersion, info.minVersion) >= 0
        ? "ready"
        : "outdated",
  };
}

// Chỉ bản 1.0.7 trở lên có /update (mở hộp thoại cập nhật của HacomSign)
export async function requestHacomSignUpdate(): Promise<boolean> {
  try {
    const response = await fetch(`${PLUGIN_URL}/update`, { method: "POST" });
    return response.ok;
  } catch {
    return false;
  }
}

// Vị trí ký chưa ký của một file, theo định dạng plugin /sign-v2 (page bắt đầu từ 1)
export function signPositionsForFile(
  pages: SignaturePage[],
  fileUrl: string
): SignPosition[] {
  return pages
    .filter((p) => p.files?.toString() === fileUrl && p.issign !== true)
    .map((p) => ({
      page: p.page + 1,
      x: p.x,
      y: p.y,
      w: p.width,
      h: p.height,
    }));
}

// Mọi file có vị trí cần ký của bước; bước ký số phải ký hết các file này rồi mới xác nhận
export function filesToSign(pages: SignaturePage[]): string[] {
  const files = pages
    .filter((p) => p.issign !== true && p.files)
    .map((p) => p.files.toString());
  return Array.from(new Set(files));
}

// Plugin tải file về, nhúng chữ ký số vào từng vị trí rồi upload đè lên đúng đường dẫn cũ
export async function signFileWithPlugin(
  fileUrl: string,
  positions: SignPosition[]
): Promise<PluginSignResult> {
  const fileName = fileUrl.split("/").pop();
  let response: Response;
  try {
    response = await fetch(`${PLUGIN_URL}/sign-v2`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileUrl, vitris: positions }),
    });
  } catch {
    return {
      ok: false,
      message:
        "Không kết nối được phần mềm ký số HacomSign. Hãy mở HacomSign rồi ký lại.",
    };
  }
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.success !== true) {
    const reason = body?.error ?? body?.Error ?? `mã lỗi ${response.status}`;
    return { ok: false, message: `Ký số file ${fileName} thất bại: ${reason}` };
  }
  return { ok: true, companyOrPerson: body.companyOrPerson ?? "" };
}
